import { useMemo, useRef } from 'react';
import { throttle } from 'lodash';
import { RefetchOptions, RefetchQueryFilters } from 'react-query';
import { isNetworkProjectType } from '../app/navigation/courier-project-type';

/** How often a list may refresh itself in response to live (SignalR) events. */
export const LIST_LIVE_REFRESH_INTERVAL_MS = 10_000;
/** Network projects aggregate every child project's events, so they refresh far less often. */
export const NETWORK_LIST_LIVE_REFRESH_INTERVAL_MS = 60_000;

/** Interval for the given project type; the default applies while the project is still loading. */
export function liveRefreshIntervalForProjectType(projectType?: string) {
  return isNetworkProjectType(projectType) ? NETWORK_LIST_LIVE_REFRESH_INTERVAL_MS : LIST_LIVE_REFRESH_INTERVAL_MS;
}

type InfiniteRefetch = (options?: RefetchOptions & RefetchQueryFilters<any>) => Promise<unknown>;

/** `refetchPage` predicate: only the first page of an infinite query is refreshed. */
export const refetchFirstPageOnly = (_page: unknown, index: number) => index === 0;

/**
 * Stable, throttled refetch of an infinite query's first page only. A plain `refetch()` re-requests
 * every loaded page in sequence, so a deep list would re-walk itself on every live event. New and
 * updated items sort to the top, so page one is all a live refresh needs.
 */
export function useThrottledFirstPageRefetch(refetch: InfiniteRefetch, intervalMs = LIST_LIVE_REFRESH_INTERVAL_MS) {
  // The query result object changes identity every render; keep the latest refetch reachable
  // without resetting the throttle window.
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  return useMemo(
    () =>
      throttle(
        () => {
          refetchRef.current({ refetchPage: refetchFirstPageOnly });
        },
        intervalMs,
        { leading: true, trailing: true }
      ),
    [intervalMs]
  );
}
